import {Controller} from "@hotwired/stimulus";
import Choices from "choices.js";

// Our version of choices.js doesn't export it's types, but we can extract them
type NotUndefined<T> = T extends undefined ? never : T; // A utility type to *unwrap* possibly undefined types
type ChoicesType = InstanceType<typeof Choices>; // Create a type for the instance of choices
type ChoicesOptions = NotUndefined<Parameters<ChoicesType["new"]>[1]>; // Type of second argument to initialize
type ClassNames = NotUndefined<ChoicesOptions["classNames"]>; // Type of classNames

// Builds the styles combined styles for a single "choice"
const buildChoiceStyles = (classNames: ClassNames, data: any) => [
  classNames.item,
  classNames.itemChoice,
  (data.disabled && classNames.itemDisabled),
  (!data.disabled && classNames.itemSelectable),
  (data.placeholder && classNames.placeholder),
].filter(Boolean).join(" ");

// We want to wrap our choice elements within a full width div (for easier mouse selection)
// This means each choice looks like a "tag", but has a footprint of a full select option
// https://github.com/Choices-js/Choices/blob/08f5313457990cad76b6889a26977ac72e8d299c/src/scripts/templates.js#L158-L174
const buildWrappedChoice = (wrapperClasses: string, classNames: ClassNames, data: any) =>
  `<div
    class="${wrapperClasses}"
    data-choice
    data-id="${data.id}"
    data-value="${data.value}"
    ${data.disabled ? "data-choice-disabled aria-disabled=\"true\"" : "data-choice-selectable"}
    id="${data.elementId}"
    role="option">
    <div class="${buildChoiceStyles(classNames, data)}">
      ${data.label}
    </div>
  </div>`;

export default class FormInputTagsController extends Controller<HTMLElement> {
  connect() {
    if (this.element.dataset.choice === "active") {
      return;
    }

    const choices = new Choices(
      this.element,
      {
        ...this.configuration(),
        callbackOnCreateTemplates: (template) => ({
          // Custom template to wrap the default choice element in a styled div
          choice: (classNames, data) => template(
            buildWrappedChoice(
              "group px-3 py-2 aria-selected:data-[choice-selectable]:bg-snowflake data-[choice-selectable]:cursor-pointer",
              classNames,
              data,
            ),
          ),
        }),
      }
    );

    (this.element as any)._choices = choices;
  }

  configuration() {
    try {
      return JSON.parse(this.data.get("configuration") || "{}");
    } catch(_) {
      return {};
    }
  }
}
